/**
 * useAutoFill — shared hook that fetches per-field auto-suggestions
 * from GET /api/projects/{pid}/auto-fill (Iter I-14 · Feb 2026).
 *
 * Behaviour:
 *   · Fetches once on mount (per projectId).
 *   · Debounced 700ms refresh whenever `sourceStamp` changes — the
 *     caller passes a stamp that changes on file re-upload or major
 *     manual edits, so stale auto-filled values never linger.
 *   · Exposes `field(name)` — returns `{value, reason, source}` or null
 *     if the backend had no data-driven inference for that field.
 *
 * Rule #1 of the spec: if there's nothing in the uploaded data to
 * infer a field from, the backend OMITS it. This hook mirrors that —
 * `field("x")` returns null, callers should render the field blank.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";

const CACHE = new Map();   // projectId → { hash, fields, ts }

export default function useAutoFill(projectId, { sourceStamp = null, debounceMs = 700 } = {}) {
  const [state, setState] = useState({ loading: false, hash: null, fields: {}, error: null });
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!projectId) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get(`/projects/${projectId}/auto-fill`);
      CACHE.set(projectId, { hash: data.source_hash, fields: data.fields || {}, ts: Date.now() });
      setState({ loading: false, hash: data.source_hash, fields: data.fields || {}, error: null });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e?.response?.data?.detail || "fetch failed" }));
    }
  }, [projectId]);

  // Initial fetch — use cache if fresh (<5s) to avoid duplicate calls when
  // the same hook is used across multiple mounted components.
  useEffect(() => {
    if (!projectId) return;
    const cached = CACHE.get(projectId);
    if (cached && Date.now() - cached.ts < 5000) {
      setState({ loading: false, hash: cached.hash, fields: cached.fields, error: null });
      return;
    }
    refresh();
  }, [projectId, refresh]);

  // Debounced re-fetch on source-stamp change (spec rule #3)
  useEffect(() => {
    if (!projectId || sourceStamp == null) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      // Invalidate cache so refresh() actually hits the backend
      CACHE.delete(projectId);
      refresh();
    }, debounceMs);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [sourceStamp, projectId, refresh, debounceMs]);

  const field = useCallback((name) => state.fields?.[name] || null, [state.fields]);

  return { ...state, field, refresh };
}
